import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { deliveryPolygonsService } from '../../services/deliveryPolygonsService';
import { DeliveryPolygon, Coordinate } from '../../types/deliveryPolygons';
import PolygonDrawer from '../map/PolygonDrawer';
import PolygonTester from '../map/PolygonTester';
import './Page.css';

interface SortablePolygonItemProps {
  polygon: DeliveryPolygon;
  index: number;
  onEdit: (polygon: DeliveryPolygon) => void;
  onDelete: (polygonId: number) => void;
  disabled: boolean;
  t: any;
}

const SortablePolygonItem: React.FC<SortablePolygonItemProps> = ({
  polygon,
  index,
  onEdit,
  onDelete,
  disabled,
  t,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: polygon.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <div
        style={{
          padding: '1rem',
          border: '1px solid #ddd',
          borderRadius: '4px',
          backgroundColor: '#f8f9fa',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
            <div
              {...listeners}
              style={{
                cursor: 'grab',
                padding: '0.5rem',
                fontSize: '1.2rem',
                userSelect: 'none',
              }}
            >
              &#8942;&#8942;
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem', fontSize: '1.1rem' }}>
                #{index + 1} - {polygon.title}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                {t('deliveryPolygons.polygonId', { id: polygon.id })}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>
                {t('deliveryPolygons.price', { price: polygon.price.toLocaleString() })}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666' }}>
                {t('deliveryPolygons.points', { count: polygon.coordinates.length })}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => onEdit(polygon)}
              disabled={disabled}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
              }}
            >
              {t('deliveryPolygons.edit')}
            </button>
            <button
              onClick={() => onDelete(polygon.id)}
              disabled={disabled}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
              }}
            >
              {t('deliveryPolygons.delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeliveryPolygons: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [polygons, setPolygons] = useState<DeliveryPolygon[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPolygon, setEditingPolygon] = useState<DeliveryPolygon | null>(null);
  const [coordinates, setCoordinates] = useState<Coordinate[]>([]);
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Priority management state
  const [hasReordered, setHasReordered] = useState(false);
  const [savingPriorities, setSavingPriorities] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchPolygons = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await deliveryPolygonsService.getAllPolygons();

      if (response.success && response.data) {
        setPolygons(response.data);
        setHasReordered(false);
      } else {
        setError(response.error || 'Failed to fetch delivery polygons');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setPolygons((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        setHasReordered(true);
        return newItems;
      });
    }
  };

  const handleSavePriorities = async () => {
    setError('');
    setSuccessMessage('');
    setSavingPriorities(true);

    try {
      const priorityUpdates = polygons.map((polygon, index) => ({
        polygonId: polygon.id,
        priority: index,
      }));

      const response = await deliveryPolygonsService.updatePolygonsPriority({
        polygons: priorityUpdates,
      });

      if (response.success) {
        setSuccessMessage(t('deliveryPolygons.prioritiesUpdated'));
        setHasReordered(false);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to update priorities');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSavingPriorities(false);
    }
  };

  useEffect(() => {
    fetchPolygons();
  }, []);

  const handleCreateNew = () => {
    setShowCreateForm(true);
    setEditingPolygon(null);
    setCoordinates([]);
    setTitle('');
    setPrice('');
    setError('');
    setSuccessMessage('');
  };

  const handleEdit = (polygon: DeliveryPolygon) => {
    setEditingPolygon(polygon);
    setShowCreateForm(true);
    setCoordinates(polygon.coordinates);
    setTitle(polygon.title);
    setPrice(polygon.price.toString());
    setError('');
    setSuccessMessage('');
  };

  const handleCancelForm = () => {
    setShowCreateForm(false);
    setEditingPolygon(null);
    setCoordinates([]);
    setTitle('');
    setPrice('');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!title.trim()) {
      setError(t('deliveryPolygons.validTitleError'));
      return;
    }

    const priceNum = parseInt(price);

    if (isNaN(priceNum) || priceNum < 0) {
      setError(t('deliveryPolygons.validPriceError'));
      return;
    }

    if (coordinates.length < 3) {
      setError(t('deliveryPolygons.minPointsError'));
      return;
    }

    setSubmitting(true);

    try {
      if (editingPolygon) {
        // Update existing polygon
        const response = await deliveryPolygonsService.updatePolygon({
          polygonId: editingPolygon.id,
          title,
          coordinates,
          price: priceNum,
        });

        if (response.success) {
          setSuccessMessage(t('deliveryPolygons.polygonUpdated'));
          setShowCreateForm(false);
          setEditingPolygon(null);
          setCoordinates([]);
          setTitle('');
          setPrice('');
          await fetchPolygons();
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setError(response.error || 'Failed to update polygon');
        }
      } else {
        // Create new polygon
        const response = await deliveryPolygonsService.createPolygon({
          title,
          coordinates,
          price: priceNum,
        });

        if (response.success) {
          setSuccessMessage(t('deliveryPolygons.polygonCreated'));
          setShowCreateForm(false);
          setCoordinates([]);
          setTitle('');
          setPrice('');
          await fetchPolygons();
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setError(response.error || 'Failed to create polygon');
        }
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (polygonId: number) => {
    if (!window.confirm(t('deliveryPolygons.deleteConfirm'))) {
      return;
    }

    setError('');
    setSuccessMessage('');

    try {
      const response = await deliveryPolygonsService.deletePolygon(polygonId);

      if (response.success) {
        setSuccessMessage(t('deliveryPolygons.polygonDeleted'));
        await fetchPolygons();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to delete polygon');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  if (loading && polygons.length === 0) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>{t('deliveryPolygons.title')}</h1>
          <p>{t('deliveryPolygons.subtitle')}</p>
        </div>
        <div className="page-content">
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            {t('deliveryPolygons.loading')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>{t('deliveryPolygons.title')}</h1>
            <p>{t('deliveryPolygons.subtitle')}</p>
          </div>
          <button
            onClick={() => navigate('/settings')}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            {t('deliveryPolygons.backToSettings')}
          </button>
        </div>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#d4edda',
              color: '#155724',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              marginBottom: '1.5rem',
            }}
          >
            {successMessage}
          </div>
        )}

        {/* Create/Edit Form */}
        {showCreateForm ? (
          <div className="detail-card full-width" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>
              {editingPolygon ? t('deliveryPolygons.editPolygon') : t('deliveryPolygons.createNewPolygon')}
            </h3>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('deliveryPolygons.polygonTitle')}
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('deliveryPolygons.polygonTitlePlaceholder')}
                  className="search-input"
                  required
                  disabled={submitting}
                  style={{ maxWidth: '300px' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('deliveryPolygons.deliveryPrice')}
                </label>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder={t('deliveryPolygons.deliveryPricePlaceholder')}
                  className="search-input"
                  required
                  disabled={submitting}
                  min="0"
                  style={{ maxWidth: '300px' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('deliveryPolygons.drawPolygonArea')}
                </label>
                <PolygonDrawer
                  initialCoordinates={coordinates}
                  onCoordinatesChange={setCoordinates}
                  existingPolygons={polygons}
                  editingPolygonId={editingPolygon?.id || null}
                  height="500px"
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  type="submit"
                  className="search-button"
                  disabled={submitting || coordinates.length < 3}
                  style={{ padding: '0.75rem 2rem' }}
                >
                  {submitting ? t('deliveryPolygons.saving') : editingPolygon ? t('deliveryPolygons.updatePolygon') : t('deliveryPolygons.createPolygon')}
                </button>
                <button
                  type="button"
                  onClick={handleCancelForm}
                  disabled={submitting}
                  style={{
                    padding: '0.75rem 2rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1,
                  }}
                >
                  {t('deliveryPolygons.cancel')}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div style={{ marginBottom: '1.5rem' }}>
            <button
              onClick={handleCreateNew}
              className="search-button"
              style={{ padding: '0.75rem 2rem' }}
            >
              {t('deliveryPolygons.createNewPolygon')}
            </button>
          </div>
        )}

        {/* Polygons List */}
        <div className="detail-card full-width" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>{t('deliveryPolygons.existingPolygons', { count: polygons.length })}</h3>
            {hasReordered && (
              <button
                onClick={handleSavePriorities}
                disabled={savingPriorities}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: savingPriorities ? 'not-allowed' : 'pointer',
                  opacity: savingPriorities ? 0.6 : 1,
                  fontWeight: 'bold',
                }}
              >
                {savingPriorities ? t('deliveryPolygons.savingPriorities') : t('deliveryPolygons.savePriorities')}
              </button>
            )}
          </div>

          {polygons.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              {t('deliveryPolygons.noPolygons')}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={polygons.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {polygons.map((polygon, index) => (
                    <SortablePolygonItem
                      key={polygon.id}
                      polygon={polygon}
                      index={index}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      disabled={showCreateForm}
                      t={t}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Polygon Tester */}
        <div className="detail-card full-width">
          <PolygonTester polygons={polygons} height="500px" />
        </div>
      </div>
    </div>
  );
};

export default DeliveryPolygons;
